import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React from 'react';
import styles from './CompetitorsAnalysis.module.css';
import { ExportOutlinedIcon } from '@mm/icons';
import {
  beautifyNumber,
  SPLIT_NUMBER_BY_THOUTHANDS,
  WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY,
} from '../WebsiteReportSummary/constants';
import { LLRActionButtons } from '../LLRActionButtons/LLRActionButtons';
import { useSelector } from 'react-redux';
import {
  websiteCompetitorsSelector,
  websitetopLandingsSelector,
} from '@mm/state/selectors/websiteReportSummarySelector';
import {
  SEMRushWebsiteCompetitorsModel,
  SEMRushWebsiteTopLandingsModel,
} from '@mm/state/model/SEMRushWebsiteTrafficModel';
import { uniqueId } from 'lodash';

type Props = {
  websiteURL: string;
  onClick: () => void;
  traffic?: number;
  isWhiteLabel?: boolean;
};

export const CompetitorsAnalysis = ({
  websiteURL,
  onClick,
  isWhiteLabel,
  traffic,
}: Props) => {
  const landings = useSelector(websitetopLandingsSelector);
  const competitors = useSelector(websiteCompetitorsSelector);
  const competitorsData = competitors?.map(item => ({
    key: uniqueId(),
    ...item,
  }));
  const landingsData = landings
    ?.filter(landing => landing.page !== websiteURL)
    .map(item => ({
      key: uniqueId(),
      ...item,
    }));

  const topLandingsColumns: ColumnsType<SEMRushWebsiteTopLandingsModel> = [
    {
      title: 'Landing Page',
      dataIndex: 'page',
      render: (page: string) => (
        <div className={styles.dataRow}>
          <span className={styles.domainName}>{page}</span>{' '}
          <a
            href={`https://${page}`}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.externalLink}
          >
            <ExportOutlinedIcon />
          </a>
        </div>
      ),
    },
    {
      title: 'Visitors',
      dataIndex: 'traffic',
      width: 300,
      render: (traffic: string) => {
        const count = Number(traffic);
        return <div className={styles.dataRow}>{beautifyNumber(count)}</div>;
      },
    },
    {
      title: 'Emails from this page',
      dataIndex: 'traffic',
      width: 200,
      render: (traffic: string) => (
        <span className={styles.dataRow}>
          {' '}
          {beautifyNumber(
            Number(traffic) * WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY,
          )}{' '}
          emails
        </span>
      ),
    },
  ];

  const competitorsColumns: ColumnsType<SEMRushWebsiteCompetitorsModel> = [
    {
      title: 'Website',
      dataIndex: 'domain',
      render: (domain: string) => (
        <div className={styles.dataRow}>
          {domain}{' '}
          <a
            href={`https://${domain}`}
            target="_blank"
            rel="noreferrer noopener"
            className={styles.externalLink}
          >
            <ExportOutlinedIcon />
          </a>
        </div>
      ),
    },
    {
      title: 'Competition Level',
      dataIndex: 'competitorRelevance',
      width: 200,
      render: (level: string) => (
        <div className={styles.dataRow}>
          {beautifyNumber(Number(level) * 100)}
        </div>
      ),
    },
    {
      title: 'Traffic',
      dataIndex: 'organicTraffic',
      width: 150,
      render: (traffic: string) => (
        <div className={styles.dataRow}>
          {traffic.replace(SPLIT_NUMBER_BY_THOUTHANDS, ',')}
        </div>
      ),
    },
    {
      title: 'Email capture with X-Ray',
      dataIndex: 'organicTraffic',
      width: 200,
      render: (traffic: string) => (
        <div className={styles.dataRow}>
          {beautifyNumber(
            Number(traffic) * WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY,
          )}{' '}
          emails
        </div>
      ),
    },
  ];

  if (!landingsData?.length && !competitorsData?.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.link}>{websiteURL}</span>'s competitor and
        landing page analysis
      </div>
      {!!landingsData?.length && (
        <div className={styles.tableContainer}>
          <div className={styles.tableTitle}>
            <span className={styles.link}>{websiteURL}</span>'s Most Popular
            Landing Pages
          </div>
          <Table
            columns={topLandingsColumns}
            dataSource={landingsData.filter((_, indx) => indx < 2)}
            pagination={false}
            className={styles.table}
          />
        </div>
      )}
      {!!competitorsData?.length && (
        <div className={styles.tableContainer}>
          <div className={styles.tableTitle}>
            <span className={styles.link}>{websiteURL}</span>'s Top 5
            Competitors
          </div>
          <Table
            columns={competitorsColumns}
            dataSource={competitorsData}
            pagination={false}
            className={styles.table}
          />
        </div>
      )}
      {!isWhiteLabel && (
        <LLRActionButtons
          onClick={onClick}
          websiteURL={websiteURL}
          traffic={traffic}
        />
      )}
    </div>
  );
};
